/**
 * OIDC per-user storage helpers for esign tokens in ReactShell.
 *
 * Storage is partitioned per authenticated user by their OIDC `sub` claim:
 *   es-<sub>:oidc.user:<authority>:esign
 *
 * Distinct from trust-shield's `ts-` prefix so the two apps coexist in the
 * same browser without their token records colliding.
 *
 * Token minting is owned by `initEsignSSO` — it POSTs to `/oidc/trusted-token`
 * with the UV SessionID and `client_id=esign`. This module is read-only: it
 * caches the sid→sub mapping (for sync callers) and exposes a UserManager
 * per sub for reading stored tokens.
 *
 * Mirror of `Components/TrustShield/oidcManager.ts`.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

const CLIENT_ID = 'esign';

const _managers = new Map<string, UserManager>();
const _sidToSub = new Map<string, string>();

function buildManager(sub: string): UserManager {
    return new UserManager({
        authority: `${window.location.origin}/oidc`,
        client_id: CLIENT_ID,
        redirect_uri: `${window.location.origin}/esign/cb`,
        scope: 'openid profile esign:read esign:write',
        response_type: 'code',
        userStore: new WebStorageStateStore({
            store: window.localStorage,
            prefix: `es-${sub}:oidc.`,
        }),
        // Refresh is driven on-demand by initEsignSSO / 401 handlers.
        automaticSilentRenew: false,
    });
}

export function getUserManager(sub: string): UserManager {
    const cached = _managers.get(sub);
    if (cached) return cached;
    const mgr = buildManager(sub);
    _managers.set(sub, mgr);
    return mgr;
}

/**
 * Per-tab in-memory cache of `uv-sid → sub` mappings. Populated by
 * `initEsignSSO` after a successful /trusted-token exchange. Sync callers
 * read through this when composing esign URLs that need `?account=<sub>`.
 */
export function getCachedSubForSid(sid: string): string | null {
    return _sidToSub.get(sid) ?? null;
}

export function cacheSubForSid(sid: string, sub: string): void {
    _sidToSub.set(sid, sub);
}
